// Fetch-based HTTP wrapper. Storefront equivalent of the BytzAPI helper used
// across other Bytz apps — same call surface, but the access token comes
// from the auth store (not cookies), unless VITE_API_KEY is configured. When
// an API key is present, requests use `Authorization: ApiKey <key>` instead.
//
// Rule: nothing outside core/api may import fetch directly. Services use
// `apiClient.get/post/...`.
import { useAuthStore } from '@core/store/authStore';
import { getRuntimeApiKey } from '@core/store/runtimeApiStore';
import { useStoreContextStore } from '@core/store/storeContextStore';
import { SESSION_EXPIRED_CODE, SESSION_EXPIRED_MESSAGE } from '@core/constants/auth';
import { env } from '@config/env';
import { APIError, normalizeError } from './errors.js';

const RETRYABLE_STATUSES = new Set([502, 503, 504]);
const API_KEY_ONLY_PREFIXES = [
  '/public/store/resolve',
  '/items',
  '/categories',
  '/countries',
  '/locations',
  '/payment-methods',
  '/account/payment-methods',
  '/plugins/payment-gateways',
  '/customers/login',
  '/customers/signup',
  '/customers/verify-otp',
  '/customers/resend-otp',
  '/customers/forgot-password',
  '/customers/reset-password',
  '/delivery',
  '/crm/referrals/availability',
  '/recipe-rules',
];
const ORG_SCOPE_PREFIXES = ['/carts', '/customers/auth/my-orders'];

function pathnameFromUrl(url) {
  const value = String(url || '');
  if (!value) return '';

  try {
    return value.startsWith('http') ? new URL(value).pathname : value;
  } catch {
    return value;
  }
}

function shouldUseApiKeyFirst(url) {
  const path = pathnameFromUrl(url);
  return API_KEY_ONLY_PREFIXES.some((prefix) => path.startsWith(prefix));
}

function shouldSendOrgScope(url) {
  const path = pathnameFromUrl(url);
  return ORG_SCOPE_PREFIXES.some((prefix) => path.startsWith(prefix));
}

function serializeQueryParams(url, params = {}) {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  );
  if (entries.length === 0) return url;
  const sep = url.includes('?') ? '&' : '?';
  const qs = entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');
  return `${url}${sep}${qs}`;
}

function buildRequest(url, reqConfig) {
  const headers = {
    'Content-Type': 'application/json',
    ...(reqConfig.headers || reqConfig.header || {}),
  };
  if (shouldSendOrgScope(reqConfig.url || url)) {
    const { companyId, brandId } = useStoreContextStore.getState();
    const orgScope =
      companyId && brandId
        ? `company=${String(companyId).trim()};brand=${String(brandId).trim()}`
        : '';

    if (orgScope && !headers['x-org-scope'] && !headers['X-Org-Scope']) {
      headers['x-org-scope'] = orgScope;
    }
  }

  if (!reqConfig.skipAuth) {
    const { accessToken, tokenType } = useAuthStore.getState();
    const apiKey = getRuntimeApiKey() || env.apiKey;
    const preferApiKey = reqConfig.preferApiKey || shouldUseApiKeyFirst(reqConfig.url || url);

    if (preferApiKey && apiKey && !headers.Authorization) {
      headers.Authorization = `ApiKey ${apiKey}`;
    } else if (accessToken && !headers.Authorization) {
      headers.Authorization = `${tokenType || 'Bearer'} ${accessToken}`;
    } else if (apiKey && !headers.Authorization) {
      headers.Authorization = `ApiKey ${apiKey}`;
    }
  }

  const finalUrl = reqConfig.query ? serializeQueryParams(url, reqConfig.query) : url;

  const requestOption = {
    method: reqConfig.method,
    headers,
    credentials: reqConfig.isCORS ? 'include' : 'same-origin',
  };

  if (reqConfig.data != null) {
    if (reqConfig.data instanceof FormData) {
      requestOption.body = reqConfig.data;
      delete headers['Content-Type'];
    } else {
      requestOption.body =
        typeof reqConfig.data !== 'string' ? JSON.stringify(reqConfig.data) : reqConfig.data;
    }
  } else if (reqConfig.body != null) {
    requestOption.body = reqConfig.body;
  }

  if (reqConfig.signal) {
    requestOption.signal = reqConfig.signal;
  }

  return new Request(finalUrl, requestOption);
}

let inFlightRefresh = null;

function shouldRetry(method, options) {
  // GET retries by default. Non-idempotent methods only retry when the caller
  // opts in explicitly with reqConfig.retry === true — otherwise we risk
  // double-charging a customer or duplicating an order on a transient blip.
  if (options.retry === false) return false;
  if (options.retry === true) return true;
  return method === 'GET';
}

function refreshOnce() {
  if (!inFlightRefresh) {
    inFlightRefresh = useAuthStore
      .getState()
      .refreshSession()
      .finally(() => {
        inFlightRefresh = null;
      });
  }
  return inFlightRefresh;
}

async function performRequest(url, options, fullURL = false) {
  // Refresh silently before the access token expires so a returning customer
  // (e.g. next day) is never bounced to login while their refresh token is valid.
  const auth = useAuthStore.getState();
  if (
    !options.skipAuth &&
    !shouldUseApiKeyFirst(url) &&
    auth.refreshToken &&
    auth.isExpiringSoon() &&
    !options._authRetry
  ) {
    try {
      await refreshOnce();
    } catch {
      // Fall through; the 401 path below decides whether to clear the session.
    }
  }

  const path = fullURL ? url : env.apiBaseUrl + url;
  const request = buildRequest(path, { ...options, url, preferApiKey: shouldUseApiKeyFirst(url) });
  const canRetry = shouldRetry(options.method, options);

  let response;
  try {
    response = await fetch(request);
  } catch (err) {
    if (canRetry && !options._transientRetry) {
      options._transientRetry = true;
      await new Promise((r) => setTimeout(r, 400));
      return performRequest(url, options, fullURL);
    }
    throw normalizeError(err);
  }

  if (
    response.status === 401 &&
    request.headers
      .get('Authorization')
      ?.startsWith(`${useAuthStore.getState().tokenType || 'Bearer'} `) &&
    !options._authRetry &&
    useAuthStore.getState().refreshToken
  ) {
    options._authRetry = true;
    try {
      await refreshOnce();
    } catch {
      // Refresh token is dead too: the user really has to sign in again.
      useAuthStore.getState().clearSession();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('open-auth-modal', {
            detail: {
              mode: 'login',
              redirect: window.location.pathname + window.location.search,
            },
          }),
        );
      }
      throw new APIError({
        code: SESSION_EXPIRED_CODE,
        message: SESSION_EXPIRED_MESSAGE,
        status: 401,
      });
    }
    return performRequest(url, options, fullURL);
  }

  if (canRetry && RETRYABLE_STATUSES.has(response.status) && !options._transientRetry) {
    options._transientRetry = true;
    await new Promise((r) => setTimeout(r, 400));
    return performRequest(url, options, fullURL);
  }

  if (response.status === 204) {
    return { success: true, data: null };
  }

  const contentType = response.headers.get('content-type') || '';
  let body = null;
  if (contentType.includes('application/json')) {
    body = await response.json().catch(() => null);
  } else {
    body = await response.text().catch(() => null);
  }

  if (!response.ok) {
    throw normalizeError({ status: response.status, body });
  }

  return body;
}

export const apiClient = {
  get: (url, options = {}) => performRequest(url, { ...options, method: 'GET' }),
  getURL: (url, options = {}) => performRequest(url, { ...options, method: 'GET' }, true),
  getBulk: (urls, options = {}) =>
    Promise.all(urls.map((u) => performRequest(u, { ...options, method: 'GET' }))),
  post: (url, options = {}) =>
    performRequest(url, { ...options, method: options.method || 'POST' }),
  postURL: (url, options = {}) =>
    performRequest(url, { ...options, method: options.method || 'POST' }, true),
  put: (url, options = {}) => performRequest(url, { ...options, method: 'PUT' }),
  update: (url, options = {}) => performRequest(url, { ...options, method: 'PUT' }),
  patch: (url, options = {}) => performRequest(url, { ...options, method: 'PATCH' }),
  delete: (url, options = {}) => performRequest(url, { ...options, method: 'DELETE' }),
};

export default apiClient;
